/**
 * TeamPortalsPage.jsx
 * Portal management hub and member directory.
 * Admin: create/delete portals, add members, deactivate accounts.
 * All roles: browse portals, view members, open individual profiles.
 */
import React, { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, doc, addDoc, setDoc, getDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, serverTimestamp, getDocs,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useTheme } from '../appContext';
import { bumpVersion } from '../utils/versionUtils';
import { loadPDFJS } from './pdfUtils';
import { useDepartments } from '../hooks/useDepartments';
import DeptMembersModal from '../components/DeptMembersModal/DeptMembersModal';
import {
  fetchMemberAttachments, isPdfAttachment, isImageAttachment,
} from '../utils/memberAttachments';

let _db = null;
const DB = () => {
  if (!_db) { try { _db = getFirestore(getApp()); } catch (_) {} }
  return _db;
};

const BUILT_IN_PORTALS = [
  { id: 'obc',      label: 'OBC Development',     icon: '🛰️', color: '#00FFB2', desc: 'CubeSat OBC — module tracker, BOM, design log, risk matrix' },
  { id: 'mp',       label: 'Mission Planning',    icon: '🛰',  color: '#6366f1', desc: 'GMAT simulations, trajectory analysis, orbital mechanics' },
  { id: 'embedded', label: 'Embedded Systems',    icon: '🔌', color: '#f59e0b', desc: 'PCB design, schematics, firmware, BOM, test results' },
  { id: 'system',   label: 'Systems Engineering', icon: '⚙',  color: '#10b981', desc: 'CAD models, FEA, thermal, mass/power budgets, ICDs' },
  { id: 'ops',      label: 'Operations',          icon: '🏛', color: '#e11d48', desc: 'Daily briefs, mission status, launch ops, anomaly reports' },
  { id: 'payload',      label: 'Payloads',            icon: '📦', color: '#0891b2', desc: 'Payload integration, mass budgets, interfaces, and mission deliverables' },
  { id: 'pe',           label: 'Prompt Engineering',  icon: '🧠',  color: '#8b5cf6', desc: 'Report portal issues, quality reviews, admin approvals' },
  { id: 'experimental', label: 'Experimental Results', icon: '🧪',  color: '#06b6d4', desc: 'Log experiment runs, track results, attach data files' },
  { id: 'mentor',       label: 'Mentor / Guest',      icon: '👨‍🏫', color: '#a855f7', desc: 'External mentors and guest advisors' },
  { id: 'admin',        label: 'Administration',      icon: '⭐',  color: '#0e9cc0', desc: 'Mission control and system administration' },
];

const BUILT_IN_PORTAL_IDS = BUILT_IN_PORTALS.map(p => p.id);

const PRESET_COLORS = [
  '#00FFB2', '#6366f1', '#f59e0b', '#10b981', '#e11d48',
  '#8b5cf6', '#a855f7', '#0e9cc0', '#ef4444', '#3b82f6',
  '#22c55e', '#f97316', '#ec4899', '#14b8a6', '#eab308',
];

const EMOJI_GRID = [
  '🚀','🛰️','🔭','🌍','🌕','⭐','🔬','🧪','💡','⚡',
  '🔧','🔩','🖥️','📡','📊','📈','🗺️','🏆','🎯','✅',
  '🛡️','🔐','💎','🌟','🔥','❄️','🌊','🍃','🧠','🤖',
  '📦','📁','🔑','🌐','⚙️','🔌','📱','💻','🎛️','🧲',
];

const ROLE_OPTIONS = [
  { id: 'obc',          label: 'OBC Development' },
  { id: 'mp',           label: 'Mission Planning' },
  { id: 'embedded',     label: 'Embedded Systems' },
  { id: 'system',       label: 'Systems Engineering' },
  { id: 'ops',          label: 'Operations' },
  { id: 'pe',           label: 'Prompt Engineering' },
  { id: 'mentor',       label: 'Mentor / Guest' },
  { id: 'admin',        label: 'Administration' },
  { id: 'experimental', label: 'Experimental Results' },
  { id: 'finance',      label: 'Finance & Admin' },
];

const SECTION_TYPES = [
  'mission_update', 'progress_report', 'test_results',
  'anomaly_report', 'design_review', 'budget_summary',
  'meeting_notes', 'technical_memo',
];

const initials = (name = '') => {
  const p = name.trim().split(/\s+/).filter(Boolean);
  return p.length >= 2
    ? (p[0][0] + p[p.length - 1][0]).toUpperCase()
    : (name.slice(0, 2).toUpperCase() || '?');
};

const fmtDate = ts => {
  if (!ts?.toDate) return '—';
  return ts.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const toSlug = str => str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

// ══════════════════════════════════════════════════════════════════════════════
//  Main page component
// ══════════════════════════════════════════════════════════════════════════════
export default function TeamPortalsPage({ role, userId, userName }) {
  const C = useTheme();
  const db = DB();
  const isAdmin = role === 'admin';
  const MONO = C.monoFont || "'Share Tech Mono', monospace";
  const SANS = C.font     || "'Inter', sans-serif";

  const [members,       setMembers]       = useState([]);
  const [customPortals, setCustomPortals] = useState([]);
  const [innerTab,      setInnerTab]      = useState('portals');
  const [detailPortal,  setDetailPortal]  = useState(null);
  const [profileMember, setProfileMember] = useState(null);
  const [showCreatePortal, setShowCreatePortal] = useState(false);
  const [addMemberPortal,  setAddMemberPortal]  = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(
      collection(db, 'authorized_users'),
      snap => setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() }))),
      () => {}
    );
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'custom_portals'), orderBy('createdAt', 'desc')),
      snap => setCustomPortals(snap.docs.map(d => ({ id: d.id, isCustom: true, ...d.data() }))),
      () => {}
    );
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Admin-created departments (Department Manager → departments/{id}) are
  // portals too; membership is the same authorized_users.portals field.
  const departments = useDepartments();
  const basePortals = [...BUILT_IN_PORTALS, ...customPortals];
  const deptPortals = departments
    .filter(d => !basePortals.some(p => p.id === d.id))
    .map(d => ({
      id: d.id,
      label: d.name || d.id,
      icon: '🏢',
      color: d.color || '#6366f1',
      desc: d.description || d.subtitle || '',
      isDepartment: true,
    }));
  const allPortals = [...basePortals, ...deptPortals];

  const portalsOf = m => (m.portals && m.portals.length ? m.portals : [m.role]);
  const membersForPortal = pid => members.filter(m => portalsOf(m).includes(pid));

  const getPortalMeta = rid =>
    allPortals.find(p => p.id === rid) || { id: rid, label: rid || 'Unknown', icon: '👤', color: C.muted };

  const filteredMembers = search.trim()
    ? members.filter(m =>
        m.name?.toLowerCase().includes(search.toLowerCase()) ||
        m.email?.toLowerCase().includes(search.toLowerCase()) ||
        portalsOf(m).some(pid => pid?.toLowerCase().includes(search.toLowerCase()))
      )
    : members;

  const toggleActive = async member => {
    if (!db) return;
    const next = member.active === false;
    try {
      await updateDoc(doc(db, 'authorized_users', member.uid), { active: next });
      setMembers(prev => prev.map(m => m.uid === member.uid ? { ...m, active: next } : m));
      setProfileMember(prev => prev?.uid === member.uid ? { ...prev, active: next } : prev);
    } catch (_) {}
  };

  const handleMemberExit = updatedMember => {
    setMembers(prev => prev.map(m => m.uid === updatedMember.uid ? { ...m, ...updatedMember } : m));
    setProfileMember(prev => prev?.uid === updatedMember.uid ? { ...prev, ...updatedMember } : prev);
  };

  const deleteCustomPortal = async portal => {
    if (!db || !portal.isCustom) return;
    if (!window.confirm(`Delete portal "${portal.label}"? Members with this role will still exist.`)) return;
    try {
      await deleteDoc(doc(db, 'custom_portals', portal.id));
      if (detailPortal?.id === portal.id) setDetailPortal(null);
    } catch (_) {}
  };

  // Departments get the full membership manager (existing members + invite);
  // other portals keep the invite-by-email pre-approval modal.
  const renderAddMember = () => addMemberPortal.isDepartment
    ? <DeptMembersModal
        dept={{ id: addMemberPortal.id, name: addMemberPortal.label, color: addMemberPortal.color }}
        userName={userName}
        onClose={() => setAddMemberPortal(null)} />
    : <AddMemberModal db={db} portal={addMemberPortal} allPortals={allPortals} userName={userName}
        C={C} MONO={MONO} SANS={SANS}
        onClose={() => setAddMemberPortal(null)} />;

  // ── Portal detail view ────────────────────────────────────────────────────────
  if (detailPortal) {
    const pm = membersForPortal(detailPortal.id);
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '24px 0 20px' }}>
          <button onClick={() => setDetailPortal(null)}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer',
              fontFamily: MONO, fontSize: 11, letterSpacing: 1 }}>← BACK</button>
          <div style={{ flex: 1, borderTop: `1px solid ${C.border}` }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 28,
          padding: '20px', background: C.panel,
          borderLeft: `4px solid ${detailPortal.color}`, border: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 40, lineHeight: 1 }}>{detailPortal.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 700,
              color: detailPortal.color, letterSpacing: 1 }}>{detailPortal.label}</div>
            {detailPortal.isCustom && (
              <span style={{ fontFamily: MONO, fontSize: 8, color: C.accent,
                background: `${C.accent}15`, border: `1px solid ${C.accent}44`,
                padding: '1px 6px', display: 'inline-block', marginTop: 4 }}>CUSTOM PORTAL</span>
            )}
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
              {detailPortal.desc || 'No description.'}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 32, fontWeight: 800,
              color: detailPortal.color, lineHeight: 1 }}>{pm.length}</div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 1 }}>MEMBERS</div>
          </div>
        </div>

        {(isAdmin || detailPortal.isDepartment) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 20 }}>
            {detailPortal.isDepartment && (
              <a href={`/dept/${detailPortal.id}`}
                style={{ padding: '9px 20px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
                  textDecoration: 'none', background: 'transparent',
                  border: `1px solid ${C.border}`, color: C.muted }}>
                OPEN DEPARTMENT →
              </a>
            )}
            {isAdmin && <button onClick={() => setAddMemberPortal(detailPortal)}
              style={{ padding: '9px 20px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
                cursor: 'pointer', background: `${detailPortal.color}18`,
                border: `1px solid ${detailPortal.color}`, color: detailPortal.color }}>
              + ADD MEMBER
            </button>}
          </div>
        )}

        {pm.length === 0 ? (
          <div style={{ fontFamily: MONO, fontSize: 12, color: C.muted,
            textAlign: 'center', padding: '60px 0' }}>
            No team members in this portal yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pm.map(m => (
              <MemberRow key={m.uid} member={m} portalMeta={detailPortal}
                C={C} MONO={MONO} SANS={SANS}
                onClick={() => setProfileMember(m)} />
            ))}
          </div>
        )}

        {profileMember && (
          <MemberProfileModal member={profileMember} allPortals={allPortals}
            C={C} MONO={MONO} SANS={SANS}
            isAdmin={isAdmin} userId={userId} userName={userName} role={role}
            onToggleActive={toggleActive}
            onMemberExit={handleMemberExit}
            onClose={() => setProfileMember(null)} />
        )}
        {addMemberPortal && renderAddMember()}
      </div>
    );
  }

  // ── Main grid / members list ──────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px 80px' }}>
      {/* Page header */}
      <div style={{ padding: '32px 0 24px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: `${C.accent}88`,
            letterSpacing: 5, marginBottom: 6 }}>SPACEMANCRAFT</div>
          <h2 style={{ margin: 0, fontFamily: MONO, fontSize: 22, fontWeight: 700,
            color: C.bright, letterSpacing: 1 }}>TEAM DIRECTORY</h2>
        </div>
        {isAdmin && innerTab === 'portals' && (
          <button onClick={() => setShowCreatePortal(true)}
            style={{ padding: '10px 22px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
              cursor: 'pointer', background: `${C.accent}18`,
              border: `1px solid ${C.accent}`, color: C.accent }}>
            + NEW PORTAL
          </button>
        )}
      </div>

      {/* Inner tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 28 }}>
        {[
          ['portals', `🏢  PORTALS (${allPortals.length})`],
          ['members', `👥  MEMBERS (${members.length})`],
        ].map(([v, l]) => (
          <button key={v} onClick={() => setInnerTab(v)}
            style={{ padding: '10px 24px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
              cursor: 'pointer', background: 'transparent', border: 'none',
              borderBottom: `2px solid ${innerTab === v ? C.accent : 'transparent'}`,
              color: innerTab === v ? C.accent : C.muted,
              transition: 'color 0.15s', whiteSpace: 'nowrap' }}>{l}</button>
        ))}
      </div>

      {/* ── PORTALS tab ─────────────────────────────────────────────────────── */}
      {innerTab === 'portals' && (
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {allPortals.map(portal => (
            <PortalCard key={portal.id} portal={portal}
              memberCount={membersForPortal(portal.id).length}
              isAdmin={isAdmin} C={C} MONO={MONO} SANS={SANS}
              onView={() => setDetailPortal(portal)}
              onAddMember={() => setAddMemberPortal(portal)}
              onDelete={() => deleteCustomPortal(portal)} />
          ))}
          {/* + New Portal card — admin only */}
          {isAdmin && (
            <AddPortalCard C={C} MONO={MONO} onClick={() => setShowCreatePortal(true)} />
          )}
        </div>
      )}

      {/* ── MEMBERS tab ─────────────────────────────────────────────────────── */}
      {innerTab === 'members' && (
        <div>
          <div style={{ marginBottom: 20, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none', fontSize: 14 }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email or portal…"
              style={{ width: '100%', background: C.panel, border: `1px solid ${C.border}`,
                color: C.text, fontFamily: MONO, fontSize: 12,
                padding: '10px 14px 10px 38px', boxSizing: 'border-box' }} />
          </div>

          {filteredMembers.length === 0 ? (
            <div style={{ fontFamily: MONO, fontSize: 12, color: C.muted,
              textAlign: 'center', padding: '60px 0' }}>
              {search ? `No members matching "${search}"` : 'No team members yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredMembers.map(m => {
                const pms = portalsOf(m).map(getPortalMeta);
                return (
                  <MemberRow key={m.uid} member={m} portalMetas={pms}
                    C={C} MONO={MONO} SANS={SANS}
                    onClick={() => setProfileMember(m)} />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Shared modals ───────────────────────────────────────────────────── */}
      {profileMember && (
        <MemberProfileModal member={profileMember} allPortals={allPortals}
          C={C} MONO={MONO} SANS={SANS}
          isAdmin={isAdmin} userId={userId} userName={userName} role={role}
          onToggleActive={toggleActive}
          onMemberExit={handleMemberExit}
          onClose={() => setProfileMember(null)} />
      )}
      {showCreatePortal && (
        <CreatePortalModal db={db} userName={userName}
          C={C} MONO={MONO}
          onClose={() => setShowCreatePortal(false)} />
      )}
      {addMemberPortal && renderAddMember()}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  + New Portal dashed card (admin, last in grid)
// ══════════════════════════════════════════════════════════════════════════════
function AddPortalCard({ C, MONO, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        border: `2px dashed ${hov ? C.accent : C.border}`,
        background: hov ? `${C.accent}08` : 'transparent',
        minHeight: 160,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s',
        gap: 8,
      }}
    >
      <div style={{ fontFamily: MONO, fontSize: 40, color: hov ? C.accent : C.muted,
        lineHeight: 1, transition: 'color 0.15s' }}>+</div>
      <div style={{ fontFamily: MONO, fontSize: 10, color: hov ? C.accent : C.muted,
        letterSpacing: 2, transition: 'color 0.15s' }}>NEW PORTAL</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Portal card in the portals grid
// ══════════════════════════════════════════════════════════════════════════════
function PortalCard({ portal, memberCount, isAdmin, C, MONO, SANS, onView, onAddMember, onDelete }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? `${portal.color}0e` : C.panel,
        border: `1px solid ${hov ? portal.color + '66' : C.border}`,
        transition: 'background 0.15s, border-color 0.15s, transform 0.12s',
        transform: hov ? 'translateY(-2px)' : 'none',
        display: 'flex', flexDirection: 'column',
        boxShadow: hov ? `0 8px 24px ${portal.color}18` : 'none',
      }}
    >
      <div style={{ height: 3, background: portal.color, opacity: 0.7 }} />

      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 26, lineHeight: 1 }}>{portal.icon}</span>
            <div>
              <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700,
                color: portal.color, letterSpacing: 0.5, lineHeight: 1.3 }}>
                {portal.label}
              </div>
              {portal.isCustom && (
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.accent,
                  background: `${C.accent}15`, border: `1px solid ${C.accent}44`,
                  padding: '1px 5px', display: 'inline-block', marginTop: 3 }}>CUSTOM</span>
              )}
              {portal.isDepartment && (
                <span style={{ fontFamily: MONO, fontSize: 8, color: portal.color,
                  background: `${portal.color}15`, border: `1px solid ${portal.color}44`,
                  padding: '1px 5px', display: 'inline-block', marginTop: 3 }}>DEPARTMENT</span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 24, fontWeight: 800,
              color: memberCount > 0 ? portal.color : C.muted, lineHeight: 1 }}>
              {memberCount}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 7, color: C.muted, letterSpacing: 1 }}>
              MEMBERS
            </div>
          </div>
        </div>

        <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted, lineHeight: 1.55,
          flex: 1, marginBottom: 14,
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {portal.desc || 'No description.'}
        </div>

        {memberCount > 0 && (
          <MemberAvatarStrip portalId={portal.id} color={portal.color} C={C} MONO={MONO} />
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}
          onClick={e => e.stopPropagation()}>
          <button onClick={onView}
            style={{ flex: 1, padding: '8px 0', fontFamily: MONO, fontSize: 9,
              letterSpacing: 1, cursor: 'pointer',
              background: `${portal.color}18`, border: `1px solid ${portal.color}77`,
              color: portal.color, transition: 'background 0.1s' }}>
            VIEW PORTAL →
          </button>
          {isAdmin && (
            <button onClick={onAddMember}
              style={{ flex: 1, padding: '8px 0', fontFamily: MONO, fontSize: 9,
                letterSpacing: 1, cursor: 'pointer', background: 'transparent',
                border: `1px solid ${C.border}`, color: C.muted,
                transition: 'border-color 0.1s, color 0.1s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = portal.color; e.currentTarget.style.color = portal.color; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}>
              + ADD MEMBER
            </button>
          )}
          {isAdmin && portal.isCustom && (
            <button onClick={onDelete}
              style={{ padding: '8px 10px', fontFamily: MONO, fontSize: 11,
                cursor: 'pointer', background: 'transparent',
                border: `1px solid #ef444444`, color: '#ef4444' }}
              title="Delete portal">🗑</button>
          )}
        </div>
      </div>
    </div>
  );
}

// Small inline strip showing up to 6 member initials for a portal
function MemberAvatarStrip({ portalId, color, C, MONO }) {
  const db = DB();
  const [mems, setMems] = useState([]);

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(
      collection(db, 'authorized_users'),
      snap => setMems(
        snap.docs.map(d => ({ uid: d.id, ...d.data() }))
          .filter(m => (m.portals && m.portals.length ? m.portals : [m.role]).includes(portalId) && m.active !== false)
          .slice(0, 6)
      ),
      () => {}
    );
    return unsub;
  }, [db, portalId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mems.length) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
      {mems.slice(0, 5).map(m => (
        <div key={m.uid} title={m.name || m.email}
          style={{ width: 24, height: 24, borderRadius: '50%',
            background: `${color}25`, border: `1.5px solid ${color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: MONO, fontSize: 8, fontWeight: 700, color, flexShrink: 0 }}>
          {initials(m.name || m.email)}
        </div>
      ))}
      {mems.length > 5 && (
        <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>+{mems.length - 5}</span>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Member row (used in members list and portal detail)
// ══════════════════════════════════════════════════════════════════════════════
function MemberRow({ member, portalMetas, portalMeta, C, MONO, SANS, onClick }) {
  const [hov, setHov] = useState(false);
  const isActive = member.active !== false;
  const metas = portalMetas || (portalMeta ? [portalMeta] : []);
  const primary = metas[0] || { color: C.muted, icon: '👤', label: '—' };

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
        background: hov ? `${primary.color}0e` : C.panel,
        border: `1px solid ${hov ? primary.color + '55' : C.border}`,
        cursor: 'pointer', transition: 'background 0.1s, border-color 0.1s',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
        background: `${primary.color}22`, border: `2px solid ${primary.color}66`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: MONO, fontSize: 13, fontWeight: 700, color: primary.color,
      }}>
        {initials(member.name || member.email)}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: C.bright,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {member.name || '—'}
        </div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {member.email || '—'}
        </div>
      </div>

      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 260 }}>
        {metas.map(pm => (
          <span key={pm.id || pm.label} style={{ fontFamily: MONO, fontSize: 10, color: pm.color,
            background: `${pm.color}15`, border: `1px solid ${pm.color}44`,
            padding: '3px 10px', whiteSpace: 'nowrap', letterSpacing: 0.5 }}>
            {pm.icon} {pm.label}
          </span>
        ))}
        <span title={isActive ? 'Active' : 'Inactive'}
          style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: isActive ? '#10b981' : '#ef4444' }} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Member profile modal
// ══════════════════════════════════════════════════════════════════════════════
const RARITY_COLOR = { Legendary: '#f59e0b', Epic: '#a78bfa', Rare: '#06b6d4', Common: '#64748b' };

function MemberAttachmentViewer({ attachment, C, MONO, onClose }) {
  const containerRef = React.useRef(null);
  const [pdfStatus, setPdfStatus] = React.useState('loading');
  const [pdfPages, setPdfPages] = React.useState(0);
  const [pdfErr, setPdfErr] = React.useState('');
  const isPdf = isPdfAttachment(attachment);
  const isImage = isImageAttachment(attachment);

  React.useEffect(() => {
    if (!isPdf || !attachment?.url) return;
    let cancelled = false;
    setPdfStatus('loading');
    setPdfPages(0);
    setPdfErr('');
    if (containerRef.current) containerRef.current.innerHTML = '';

    async function renderPdf() {
      try {
        const pdfjs = await loadPDFJS();
        const pdf = await pdfjs.getDocument({ url: attachment.url }).promise;
        if (cancelled) return;
        setPdfPages(pdf.numPages);
        for (let p = 1; p <= pdf.numPages; p++) {
          if (cancelled) break;
          const page = await pdf.getPage(p);
          const vp = page.getViewport({ scale: 1.4 });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width;
          canvas.height = vp.height;
          canvas.style.cssText = 'width:100%;display:block;margin-bottom:8px;border-radius:4px';
          if (containerRef.current && !cancelled) containerRef.current.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        }
        if (!cancelled) setPdfStatus('done');
      } catch (err) {
        if (!cancelled) {
          setPdfErr(err.message || 'Failed to load PDF');
          setPdfStatus('error');
        }
      }
    }

    renderPdf();
    return () => { cancelled = true; };
  }, [attachment?.url, isPdf]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 900,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: C.panel, border: `1px solid ${C.border}`,
        width: '100%', maxWidth: 760, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px #000000bb',
      }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 16 }}>{attachment.icon || '📎'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 11, color: C.bright, fontWeight: 700 }}>
              {attachment.label}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {attachment.name}
            </div>
          </div>
          <a href={attachment.url} target="_blank" rel="noreferrer"
            style={{ fontFamily: MONO, fontSize: 9, color: C.accent, textDecoration: 'none', flexShrink: 0 }}>
            OPEN ↗
          </a>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: C.muted,
              cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: C.bg || C.eqBg }}>
          {isImage && (
            <img src={attachment.url} alt={attachment.name}
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 6 }} />
          )}

          {isPdf && (
            <>
              {pdfStatus === 'loading' && (
                <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, padding: '24px 0', textAlign: 'center' }}>
                  Rendering PDF…
                </div>
              )}
              {pdfStatus === 'error' && (
                <div style={{ fontFamily: MONO, fontSize: 10, color: '#ef4444', marginBottom: 12 }}>
                  {pdfErr} — <a href={attachment.url} target="_blank" rel="noreferrer" style={{ color: C.accent }}>open directly</a>
                </div>
              )}
              {pdfPages > 0 && (
                <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginBottom: 8 }}>
                  {pdfPages} page{pdfPages > 1 ? 's' : ''}
                </div>
              )}
              <div ref={containerRef} />
            </>
          )}

          {!isImage && !isPdf && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, marginBottom: 16 }}>
                Preview not available for this file type.
              </div>
              <a href={attachment.url} target="_blank" rel="noreferrer"
                style={{ fontFamily: MONO, fontSize: 10, color: C.accent, textDecoration: 'none',
                  border: `1px solid ${C.accent}55`, padding: '10px 18px', display: 'inline-block' }}>
                VIEW FILE ↗
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MemberExitDialog({ member, db, userId, userName, C, MONO, onClose, onSaved }) {
  const memberUid = member?.uid || member?.id;
  const [exitType, setExitType] = useState('left');
  const [details, setDetails] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  function addFiles(fileList) {
    const next = Array.from(fileList || []).filter(f => f && f.size > 0);
    if (!next.length) return;
    setPendingFiles(prev => [...prev, ...next]);
  }

  function removeFile(index) {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }

  async function uploadAttachments() {
    if (!pendingFiles.length) return [];
    const storage = getStorage(getApp());
    const uploaded = [];
    for (const file of pendingFiles) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `member_exits/${memberUid}/${Date.now()}_${safeName}`;
      const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type || 'application/octet-stream' });
      await new Promise((resolve, reject) => task.on('state_changed', null, reject, resolve));
      const url = await getDownloadURL(task.snapshot.ref);
      uploaded.push({ name: file.name, url, size: file.size, type: file.type || 'application/octet-stream' });
    }
    return uploaded;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!details.trim()) {
      setErr('Please enter details about the departure.');
      return;
    }
    if (!db || !memberUid) return;

    setErr('');
    setBusy(true);
    try {
      const attachments = await uploadAttachments();
      const payload = {
        active: false,
        exitType,
        exitDetails: details.trim(),
        exitAt: serverTimestamp(),
        exitRecordedBy: userName || 'Admin',
        exitRecordedByUid: userId || null,
        exitAttachments: attachments,
      };
      await updateDoc(doc(db, 'authorized_users', memberUid), payload);
      await addDoc(collection(db, 'member_exit_records'), {
        memberUid,
        memberName: member.name || '',
        memberEmail: member.email || '',
        exitType,
        details: details.trim(),
        attachments,
        recordedBy: userName || 'Admin',
        recordedByUid: userId || null,
        createdAt: serverTimestamp(),
      });
      onSaved({
        ...member,
        active: false,
        exitType,
        exitDetails: details.trim(),
        exitAttachments: attachments,
      });
      onClose();
    } catch (ex) {
      setErr(ex.message || 'Could not save departure record.');
    } finally {
      setBusy(false);
    }
  }

  const exitLabel = exitType === 'laid_off' ? 'LAID OFF' : 'LEFT THE COMPANY';

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#000000bb', zIndex: 750,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && !busy && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        style={{ background: C.panel, border: `1px solid ${C.border}`, width: '100%', maxWidth: 480,
          boxShadow: '0 24px 64px #000000bb' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: C.eqBg || C.bg }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 2 }}>
            RECORD DEPARTURE
          </div>
          <button type="button" onClick={onClose} disabled={busy}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: busy ? 'wait' : 'pointer',
              fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontFamily: MONO, fontSize: 11, color: C.text, lineHeight: 1.5 }}>
            Record departure for <strong style={{ color: C.bright }}>{member.name || member.email}</strong>.
            Their account will be deactivated and this record will be saved with your notes and attachments.
          </div>

          <div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>
              DEPARTURE TYPE
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { id: 'left', label: '🚪 LEFT THE COMPANY' },
                { id: 'laid_off', label: '⊘ LAID OFF' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setExitType(opt.id)}
                  style={{
                    flex: '1 1 180px', padding: '10px 12px', cursor: 'pointer',
                    fontFamily: MONO, fontSize: 9, letterSpacing: 0.5,
                    background: exitType === opt.id ? '#ef444418' : 'transparent',
                    border: `1px solid ${exitType === opt.id ? '#ef4444' : C.border}`,
                    color: exitType === opt.id ? '#ef4444' : C.muted,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>
              DETAILS *
            </div>
            <textarea
              value={details}
              onChange={e => setDetails(e.target.value)}
              placeholder={`Reason, last working day, handover notes, ${exitLabel.toLowerCase()} context…`}
              rows={5}
              required
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'vertical',
                background: C.eqBg || C.bg, border: `1px solid ${C.border}`, color: C.text,
                padding: '10px 12px', fontFamily: MONO, fontSize: 11, lineHeight: 1.5, outline: 'none',
              }}
            />
          </div>

          <div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>
              ATTACHMENTS (OPTIONAL)
            </div>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
              fontFamily: MONO, fontSize: 9, letterSpacing: 1, cursor: busy ? 'wait' : 'pointer',
              background: `${C.accent}12`, border: `1px dashed ${C.accent}66`, color: C.accent,
            }}>
              + ADD FILES
              <input
                type="file"
                multiple
                disabled={busy}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                style={{ display: 'none' }}
              />
            </label>
            {pendingFiles.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {pendingFiles.map((file, i) => (
                  <div key={`${file.name}-${i}`} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                    background: C.eqBg || C.bg, border: `1px solid ${C.border}`,
                  }}>
                    <span style={{ fontSize: 14 }}>📎</span>
                    <div style={{ flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 10, color: C.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.name}
                    </div>
                    <button type="button" onClick={() => removeFile(i)} disabled={busy}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer',
                        fontFamily: MONO, fontSize: 9 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {err && (
            <div style={{ fontFamily: MONO, fontSize: 10, color: '#fecaca', background: '#7f1d1d55',
              border: '1px solid #ef444444', padding: '8px 12px', borderRadius: 4 }}>
              {err}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="button" onClick={onClose} disabled={busy}
              style={{ flex: 1, padding: '10px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
                cursor: busy ? 'wait' : 'pointer', background: 'transparent',
                border: `1px solid ${C.border}`, color: C.muted }}>
              CANCEL
            </button>
            <button type="submit" disabled={busy}
              style={{ flex: 2, padding: '10px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
                cursor: busy ? 'wait' : 'pointer', background: '#ef444418',
                border: '1px solid #ef4444', color: '#ef4444' }}>
              {busy ? 'SAVING…' : `CONFIRM · ${exitLabel}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function MemberProfileModal({ member, allPortals, C, MONO, SANS, isAdmin, userId, userName, role, onToggleActive, onMemberExit, onClose }) {
  const portal = allPortals.find(p => p.id === member.role)
    || { id: member.role, label: member.role || 'Unknown', icon: '👤', color: C.muted };
  const isActive = member.active !== false;

  const memberUid = member?.uid || member?.id;
  const canViewDocs = isAdmin || role === 'finance' || memberUid === userId;

  const [badges, setBadges] = React.useState([]);
  const [tooltip, setTooltip] = React.useState(null);
  const [attachments, setAttachments] = React.useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = React.useState(false);
  const [viewingAttachment, setViewingAttachment] = React.useState(null);
  const [showExitDialog, setShowExitDialog] = React.useState(false);
  let db;
  try { db = getFirestore(getApp()); } catch (_) { db = null; }

  React.useEffect(() => {
    if (!memberUid) return;
    let db;
    try { db = getFirestore(getApp()); } catch (_) { return; }
    getDocs(collection(db, 'user_badges', memberUid, 'earned')).then(snap => {
      const earned = snap.docs.map(d => d.data());
      getDocs(collection(db, 'badge_definitions')).then(defSnap => {
        const defs = {};
        defSnap.docs.forEach(d => { defs[d.id] = d.data(); });
        const merged = earned.map(e => ({ ...e, ...defs[e.badgeId] })).filter(e => e.name || e.badgeName);
        setBadges(merged);
      }).catch(() => {});
    }).catch(() => {});
  }, [memberUid]);

  React.useEffect(() => {
    if (!canViewDocs || !memberUid) {
      setAttachments([]);
      return;
    }
    let cancelled = false;
    let db;
    try { db = getFirestore(getApp()); } catch (_) { return; }

    setAttachmentsLoading(true);
    fetchMemberAttachments(db, member)
      .then(items => { if (!cancelled) setAttachments(items); })
      .catch(() => { if (!cancelled) setAttachments([]); })
      .finally(() => { if (!cancelled) setAttachmentsLoading(false); });

    return () => { cancelled = true; };
  }, [member, memberUid, canViewDocs]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#00000099', zIndex: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: C.panel, border: `1px solid ${C.border}`,
        width: '100%', maxWidth: 440, boxShadow: '0 24px 64px #000000bb' }}>

        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: C.eqBg || C.bg }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 2 }}>
            MEMBER PROFILE
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: C.muted,
              cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        <div style={{ padding: '28px 24px 24px', maxHeight: '72vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column',
            alignItems: 'center', marginBottom: 24 }}>
            <div style={{
              width: 88, height: 88, borderRadius: '50%', marginBottom: 16,
              background: `${portal.color}22`, border: `3px solid ${portal.color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: MONO, fontSize: 28, fontWeight: 700, color: portal.color,
            }}>
              {initials(member.name || member.email)}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 700, color: C.bright,
              textAlign: 'center', letterSpacing: 0.5 }}>
              {member.name || '—'}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, marginTop: 5 }}>
              {member.email || '—'}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
            <ProfileDetailRow label="PORTAL" C={C} MONO={MONO}>
              <span style={{ fontFamily: MONO, fontSize: 11, color: portal.color,
                background: `${portal.color}15`, border: `1px solid ${portal.color}44`,
                padding: '3px 10px' }}>
                {portal.icon} {portal.label}
              </span>
            </ProfileDetailRow>

            <ProfileDetailRow label="STATUS" C={C} MONO={MONO}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%',
                  background: isActive ? '#10b981' : '#ef4444' }} />
                <span style={{ fontFamily: MONO, fontSize: 11,
                  color: isActive ? '#10b981' : '#ef4444' }}>
                  {isActive ? 'ACTIVE' : (member.exitType === 'laid_off' ? 'LAID OFF' : member.exitType === 'left' ? 'LEFT COMPANY' : 'INACTIVE')}
                </span>
              </span>
            </ProfileDetailRow>

            {!isActive && member.exitDetails && (
              <ProfileDetailRow label="DEPARTURE" C={C} MONO={MONO}>
                <span style={{ fontFamily: MONO, fontSize: 10, color: C.text, lineHeight: 1.45,
                  whiteSpace: 'pre-wrap' }}>
                  {member.exitDetails}
                </span>
              </ProfileDetailRow>
            )}

            {!isActive && Array.isArray(member.exitAttachments) && member.exitAttachments.length > 0 && (
              <ProfileDetailRow label="EXIT FILES" C={C} MONO={MONO}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {member.exitAttachments.map((item, i) => (
                    <a key={`${item.url}-${i}`} href={item.url} target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily: MONO, fontSize: 10, color: C.accent, textDecoration: 'none' }}>
                      📎 {item.name || 'Attachment'}
                    </a>
                  ))}
                </div>
              </ProfileDetailRow>
            )}

            <ProfileDetailRow label="MEMBER SINCE" C={C} MONO={MONO}>
              <span style={{ fontFamily: MONO, fontSize: 11, color: C.text }}>
                {fmtDate(member.approvedAt)}
              </span>
            </ProfileDetailRow>

            {member.approvedBy && (
              <ProfileDetailRow label="APPROVED BY" C={C} MONO={MONO}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: C.text }}>
                  {member.approvedBy}
                </span>
              </ProfileDetailRow>
            )}
          </div>

          {canViewDocs && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>
                DOCUMENTS
              </div>
              {attachmentsLoading ? (
                <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, padding: '8px 12px',
                  background: C.eqBg || C.bg, border: `1px solid ${C.eqBorder || C.border}` }}>
                  Loading documents…
                </div>
              ) : attachments.length === 0 ? (
                <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, padding: '8px 12px',
                  background: C.eqBg || C.bg, border: `1px solid ${C.eqBorder || C.border}` }}>
                  No documents on file.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {attachments.map((item, i) => (
                    <button
                      key={`${item.url}-${i}`}
                      type="button"
                      onClick={() => setViewingAttachment(item)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 12px', cursor: 'pointer', textAlign: 'left',
                        background: C.eqBg || C.bg,
                        border: `1px solid ${C.eqBorder || C.border}`,
                      }}
                    >
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon || '📎'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: MONO, fontSize: 10, color: C.bright, fontWeight: 600 }}>
                          {item.label}
                        </div>
                        <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.name}
                        </div>
                      </div>
                      <span style={{ fontFamily: MONO, fontSize: 9, color: C.accent, flexShrink: 0 }}>VIEW →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Badges */}
          {badges.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>BADGES</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {badges.map((b, i) => {
                  const rc = RARITY_COLOR[b.rarity] || '#64748b';
                  const isLeg = b.rarity === 'Legendary';
                  return (
                    <div key={i} onClick={() => setTooltip(tooltip === i ? null : i)}
                      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 10px', cursor: 'pointer',
                        background: `${rc}18`,
                        border: `1px solid ${rc}${isLeg ? 'aa' : '55'}`,
                        animation: isLeg ? 'legBadgeGlow 2s ease-in-out infinite' : undefined }}>
                      <style>{`@keyframes legBadgeGlow{0%,100%{box-shadow:0 0 4px ${rc}44}50%{box-shadow:0 0 12px ${rc}88}}`}</style>
                      <span style={{ fontSize: 12 }}>{b.icon || '🏅'}</span>
                      <span style={{ fontFamily: MONO, fontSize: 9, color: rc }}>{b.name || b.badgeName}</span>
                      {tooltip === i && (
                        <div style={{ position: 'absolute', bottom: '110%', left: 0, zIndex: 10,
                          background: '#1e2a43', border: '1px solid rgba(255,255,255,0.1)',
                          padding: '8px 12px', minWidth: 180 }}>
                          <div style={{ fontFamily: MONO, fontSize: 9, color: '#d9e2ff', marginBottom: 3 }}>{b.name || b.badgeName}</div>
                          <div style={{ fontFamily: 'Inter,sans-serif', fontSize: 10, color: '#bdc8ce', marginBottom: 3 }}>{b.description}</div>
                          {b.earnedAt?.toDate && (
                            <div style={{ fontFamily: MONO, fontSize: 8, color: '#64748b' }}>
                              {b.earnedAt.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isAdmin && isActive && (
            <button type="button" onClick={() => setShowExitDialog(true)}
              style={{
                width: '100%', padding: '10px', marginBottom: 8, fontFamily: MONO, fontSize: 10,
                letterSpacing: 1, cursor: 'pointer',
                background: '#f9731612',
                border: '1px solid #f97316',
                color: '#f97316',
              }}>
              🚪 LEFT / LAID OFF FROM COMPANY
            </button>
          )}

          {isAdmin && (
            <button onClick={() => onToggleActive(member)}
              style={{
                width: '100%', padding: '10px', fontFamily: MONO, fontSize: 10,
                letterSpacing: 1, cursor: 'pointer',
                background: isActive ? '#ef444412' : '#10b98112',
                border: `1px solid ${isActive ? '#ef4444' : '#10b981'}`,
                color: isActive ? '#ef4444' : '#10b981',
              }}>
              {isActive ? '⊗ DEACTIVATE ACCOUNT' : '✓ REACTIVATE ACCOUNT'}
            </button>
          )}
        </div>
      </div>

      {viewingAttachment && (
        <MemberAttachmentViewer
          attachment={viewingAttachment}
          C={C}
          MONO={MONO}
          onClose={() => setViewingAttachment(null)}
        />
      )}

      {showExitDialog && db && onMemberExit && (
        <MemberExitDialog
          member={member}
          db={db}
          userId={userId}
          userName={userName}
          C={C}
          MONO={MONO}
          onClose={() => setShowExitDialog(false)}
          onSaved={onMemberExit}
        />
      )}
    </div>
  );
}

function ProfileDetailRow({ label, children, C, MONO }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 12px',
      background: C.eqBg || C.bg, border: `1px solid ${C.eqBorder || C.border}` }}>
      <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted,
        letterSpacing: 1.5, width: 110, flexShrink: 0 }}>{label}</div>
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Create portal modal — 4-section rich form (admin only)
// ══════════════════════════════════════════════════════════════════════════════
function CreatePortalModal({ db, userName, C, MONO, onClose }) {
  const SECTIONS = ['IDENTITY', 'FEATURES', 'ACCESS & MEMBERS', 'REPORT BUILDER'];
  const [section, setSection] = useState(0);

  // Section 0 — Identity
  const [label,    setLabel]    = useState('');
  const [portalId, setPortalId] = useState('');
  const [idManual, setIdManual] = useState(false);
  const [desc,     setDesc]     = useState('');
  const [icon,     setIcon]     = useState('🚀');
  const [iconText, setIconText] = useState('');
  const [color,    setColor]    = useState('#6366f1');
  const [idError,  setIdError]  = useState('');

  // Section 1 — Features
  const [hasFeed,        setHasFeed]        = useState(false);
  const [hasTaskBoard,   setHasTaskBoard]   = useState(false);
  const [hasProjects,    setHasProjects]    = useState(false);
  const [hasCADViewer,   setHasCADViewer]   = useState(false);
  const [hasExperiments, setHasExperiments] = useState(false);

  // Section 2 — Access & Members
  const [allowedRoles,        setAllowedRoles]        = useState([]);
  const [defaultView,         setDefaultView]         = useState('feed');
  const [initialMembersInput, setInitialMembersInput] = useState('');
  const [initialMembers,      setInitialMembers]      = useState([]);

  // Section 3 — Report Builder
  const [allowedSectionTypes, setAllowedSectionTypes] = useState([]);
  const [requireApproval,     setRequireApproval]     = useState(true);

  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  // Auto-slug label → portalId
  useEffect(() => {
    if (!idManual) setPortalId(toSlug(label));
  }, [label, idManual]);

  // Uniqueness check
  useEffect(() => {
    if (!portalId) { setIdError(''); return; }
    if (BUILT_IN_PORTAL_IDS.includes(portalId)) {
      setIdError('Conflicts with a built-in portal ID');
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'custom_portals', portalId))
      .then(snap => { if (!cancelled) setIdError(snap.exists() ? 'This portal ID already exists' : ''); })
      .catch(() => { if (!cancelled) setIdError(''); });
    return () => { cancelled = true; };
  }, [portalId, db]);

  const addMemberTag = () => {
    const email = initialMembersInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (!initialMembers.includes(email)) setInitialMembers(p => [...p, email]);
    setInitialMembersInput('');
  };

  const toggleRole = id =>
    setAllowedRoles(p => p.includes(id) ? p.filter(r => r !== id) : [...p, id]);

  const toggleSectionType = t =>
    setAllowedSectionTypes(p => p.includes(t) ? p.filter(s => s !== t) : [...p, t]);

  async function submit() {
    if (!label.trim()) { setErr('Portal name is required'); setSection(0); return; }
    if (!portalId)     { setErr('Portal ID cannot be empty'); setSection(0); return; }
    if (idError)       { setErr(idError); setSection(0); return; }
    setBusy(true); setErr('');
    try {
      await setDoc(doc(db, 'custom_portals', portalId), {
        label: label.trim(),
        icon:  icon.trim() || '🚀',
        color,
        desc:  desc.trim(),
        hasFeed,
        feedCollection:      hasFeed ? `${portalId}_posts` : null,
        hasTaskBoard,
        hasProjects,
        hasCADViewer,
        hasExperiments,
        allowedRoles,
        defaultView,
        requireApproval,
        allowedSectionTypes,
        createdAt: serverTimestamp(),
        createdBy: userName || 'Admin',
      });

      // Pre-approve initial members
      for (const email of initialMembers) {
        await addDoc(collection(db, 'access_requests'), {
          uid: null, email,
          team: portalId,
          reason: `Pre-approved for ${label.trim()} portal`,
          status: 'pre_approved',
          role: portalId,
          preApprovedEmail: email,
          requestedAt: serverTimestamp(),
          approvedAt:  serverTimestamp(),
          approvedBy:  userName || 'Admin',
        });
      }

      // Notify admin
      await addDoc(collection(db, 'notifications'), {
        type: 'portal_created',
        recipientId: 'admin',
        title:   `New Portal Created: ${label.trim()}`,
        message: `Portal "${label.trim()}" (${portalId}) created with ${initialMembers.length} pre-approved member(s).`,
        read: false,
        createdAt: serverTimestamp(),
      });

      await bumpVersion(`Portal created: ${label.trim()} (${portalId})`, userName || 'Admin');

      onClose();
    } catch (ex) {
      setErr(ex.message || 'Failed to create portal');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = !busy && !!label.trim() && !!portalId && !idError;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#00000099', zIndex: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: C.panel, border: `1px solid ${C.border}`,
        width: '100%', maxWidth: 560, boxShadow: '0 24px 64px #000000bb',
        maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: C.eqBg || C.bg, flexShrink: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.accent, letterSpacing: 2 }}>
            CREATE NEW PORTAL
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: C.muted,
              cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Section tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {SECTIONS.map((s, i) => (
            <button key={i} onClick={() => setSection(i)}
              style={{ flex: 1, padding: '9px 4px', fontFamily: MONO, fontSize: 8,
                letterSpacing: 0.5, cursor: 'pointer', background: 'transparent', border: 'none',
                borderBottom: `2px solid ${section === i ? C.accent : 'transparent'}`,
                color: section === i ? C.accent : C.muted, transition: 'color 0.15s' }}>
              {i + 1}. {s}
            </button>
          ))}
        </div>

        {/* Live preview banner */}
        <div style={{ padding: '9px 16px', background: `${color}12`,
          borderBottom: `2px solid ${color}55`,
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <div>
            <span style={{ fontFamily: MONO, fontSize: 13, color, fontWeight: 700 }}>
              {label || 'Portal Name'}
            </span>
            {portalId && (
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginLeft: 8 }}>
                /{portalId}
              </span>
            )}
          </div>
        </div>

        {/* Scrollable section content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* ── 0: Identity ── */}
          {section === 0 && (
            <div style={{ padding: '20px' }}>
              <FieldLabel MONO={MONO} C={C}>PORTAL NAME *</FieldLabel>
              <input value={label} onChange={e => setLabel(e.target.value)}
                placeholder="e.g. Ground Station"
                style={inputStyle(C, MONO, 13)} />

              <FieldLabel MONO={MONO} C={C}>PORTAL ID (URL SLUG) *</FieldLabel>
              <div style={{ marginBottom: 16 }}>
                <input value={portalId}
                  onChange={e => {
                    setIdManual(true);
                    setPortalId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                  }}
                  placeholder="auto-generated-from-name"
                  style={{ ...inputStyle(C, MONO, 12), marginBottom: 0,
                    borderColor: idError ? '#ef4444' : (portalId && !idError ? '#10b981' : C.border) }} />
                {idError && (
                  <div style={{ fontFamily: MONO, fontSize: 9, color: '#ef4444', marginTop: 4 }}>{idError}</div>
                )}
                {portalId && !idError && (
                  <div style={{ fontFamily: MONO, fontSize: 9, color: '#10b981', marginTop: 4 }}>✓ Available</div>
                )}
              </div>

              <FieldLabel MONO={MONO} C={C}>DESCRIPTION</FieldLabel>
              <textarea value={desc} onChange={e => setDesc(e.target.value)}
                placeholder="Brief description of this portal's purpose…"
                rows={2}
                style={{ ...inputStyle(C, MONO, 11), resize: 'vertical', lineHeight: 1.55 }} />

              <FieldLabel MONO={MONO} C={C}>EMOJI / ICON</FieldLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10,
                padding: '8px', background: C.bg, border: `1px solid ${C.border}` }}>
                {EMOJI_GRID.map(em => (
                  <button key={em} type="button"
                    onClick={() => { setIcon(em); setIconText(''); }}
                    style={{ width: 32, height: 32, fontSize: 18, cursor: 'pointer',
                      background: icon === em ? `${color}22` : 'transparent',
                      border: `1px solid ${icon === em ? color : 'transparent'}`,
                      borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {em}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input value={iconText}
                  onChange={e => { setIconText(e.target.value); if (e.target.value) setIcon(e.target.value); }}
                  placeholder="Or type an emoji…" maxLength={4}
                  style={{ ...inputStyle(C, MONO, 20), flex: 1, textAlign: 'center', marginBottom: 0 }} />
              </div>

              <FieldLabel MONO={MONO} C={C}>COLOR</FieldLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 2 }}>
                {PRESET_COLORS.map(col => (
                  <button key={col} type="button" onClick={() => setColor(col)}
                    style={{ position: 'relative', width: 28, height: 28, borderRadius: '50%',
                      background: col, cursor: 'pointer', padding: 0,
                      border: `2px solid ${color === col ? '#fff' : col + '44'}`,
                      outline: color === col ? `2px solid ${col}` : 'none',
                      outlineOffset: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {color === col && (
                      <span style={{ color: '#000', fontSize: 13, fontWeight: 900, lineHeight: 1 }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── 1: Features ── */}
          {section === 1 && (
            <div style={{ padding: '20px' }}>
              <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, lineHeight: 1.7,
                marginBottom: 16 }}>
                Enable modules this portal needs. Disabled modules won't appear in portal navigation.
              </div>
              {[
                { key: 'hasFeed',        label: 'Social Feed',   desc: 'Team posts, announcements and updates',            val: hasFeed,        set: setHasFeed },
                { key: 'hasTaskBoard',   label: 'Task Board',    desc: 'Kanban-style task management',                     val: hasTaskBoard,   set: setHasTaskBoard },
                { key: 'hasProjects',    label: 'Projects',      desc: 'Project milestones and deliverables tracking',     val: hasProjects,    set: setHasProjects },
                { key: 'hasCADViewer',   label: 'CAD Viewer',    desc: '3D model viewer for STEP / IGES files',            val: hasCADViewer,   set: setHasCADViewer },
                { key: 'hasExperiments', label: 'Experiments',   desc: 'Experiment log linked to ExperimentalResultsPortal', val: hasExperiments, set: setHasExperiments },
              ].map(f => (
                <div key={f.key} onClick={() => f.set(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                    marginBottom: 8, cursor: 'pointer',
                    background: f.val ? `${color}0e` : C.bg,
                    border: `1px solid ${f.val ? color + '55' : C.border}`,
                    transition: 'background 0.1s, border-color 0.1s' }}>
                  <div style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                    background: f.val ? color : 'transparent',
                    border: `2px solid ${f.val ? color : C.muted}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {f.val && <span style={{ color: '#000', fontSize: 12, fontWeight: 900 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 600,
                      color: f.val ? color : C.text }}>{f.label}</div>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── 2: Access & Members ── */}
          {section === 2 && (
            <div style={{ padding: '20px' }}>
              <FieldLabel MONO={MONO} C={C}>ALLOWED ROLES</FieldLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18,
                padding: '10px', background: C.bg, border: `1px solid ${C.border}` }}>
                {ROLE_OPTIONS.map(r => {
                  const sel = allowedRoles.includes(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggleRole(r.id)}
                      style={{ padding: '5px 12px', fontFamily: MONO, fontSize: 9,
                        letterSpacing: 0.5, cursor: 'pointer', borderRadius: 3,
                        background: sel ? `${color}22` : 'transparent',
                        border: `1px solid ${sel ? color : C.border}`,
                        color: sel ? color : C.muted, transition: 'background 0.1s' }}>
                      {r.label}
                    </button>
                  );
                })}
              </div>

              <FieldLabel MONO={MONO} C={C}>DEFAULT VIEW</FieldLabel>
              <select value={defaultView} onChange={e => setDefaultView(e.target.value)}
                style={{ ...inputStyle(C, MONO, 12), cursor: 'pointer' }}>
                <option value="feed">Feed</option>
                <option value="tasks">Task Board</option>
                <option value="projects">Projects</option>
                <option value="experiments">Experiments</option>
                <option value="reports">Reports</option>
              </select>

              <FieldLabel MONO={MONO} C={C}>INITIAL MEMBERS (pre-approve by email)</FieldLabel>
              {initialMembers.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8,
                  padding: '6px', background: C.bg, border: `1px solid ${C.border}` }}>
                  {initialMembers.map(email => (
                    <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontFamily: MONO, fontSize: 10, color,
                      background: `${color}15`, border: `1px solid ${color}44`,
                      padding: '3px 8px', borderRadius: 3 }}>
                      {email}
                      <button type="button"
                        onClick={() => setInitialMembers(p => p.filter(e => e !== email))}
                        style={{ background: 'none', border: 'none', color: C.muted,
                          cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input value={initialMembersInput}
                  onChange={e => setInitialMembersInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addMemberTag(); } }}
                  placeholder="email@example.com — Enter or , to add"
                  style={{ ...inputStyle(C, MONO, 11), flex: 1, marginBottom: 0 }} />
                <button type="button" onClick={addMemberTag}
                  style={{ padding: '0 14px', fontFamily: MONO, fontSize: 9, cursor: 'pointer',
                    background: `${color}18`, border: `1px solid ${color}`, color,
                    whiteSpace: 'nowrap' }}>+ ADD</button>
              </div>
              {initialMembers.length > 0 && (
                <div style={{ fontFamily: MONO, fontSize: 9, color: '#10b981', marginBottom: 8 }}>
                  {initialMembers.length} member{initialMembers.length > 1 ? 's' : ''} will be pre-approved on creation.
                </div>
              )}
            </div>
          )}

          {/* ── 3: Report Builder ── */}
          {section === 3 && (
            <div style={{ padding: '20px' }}>
              <FieldLabel MONO={MONO} C={C}>ALLOWED REPORT SECTION TYPES</FieldLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20,
                padding: '8px', background: C.bg, border: `1px solid ${C.border}` }}>
                {SECTION_TYPES.map(type => {
                  const sel = allowedSectionTypes.includes(type);
                  return (
                    <div key={type} onClick={() => toggleSectionType(type)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 10px', cursor: 'pointer',
                        background: sel ? `${color}0e` : 'transparent',
                        border: `1px solid ${sel ? color + '44' : 'transparent'}`,
                        transition: 'background 0.1s' }}>
                      <div style={{ width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                        background: sel ? color : 'transparent',
                        border: `2px solid ${sel ? color : C.muted}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {sel && <span style={{ color: '#000', fontSize: 10, fontWeight: 900 }}>✓</span>}
                      </div>
                      <span style={{ fontFamily: MONO, fontSize: 10,
                        color: sel ? color : C.text, letterSpacing: 0.5,
                        textTransform: 'uppercase' }}>
                        {type.replace(/_/g, ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>

              <FieldLabel MONO={MONO} C={C}>APPROVAL POLICY</FieldLabel>
              <div onClick={() => setRequireApproval(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                  cursor: 'pointer',
                  background: requireApproval ? `${color}0e` : C.bg,
                  border: `1px solid ${requireApproval ? color + '55' : C.border}`,
                  transition: 'background 0.1s' }}>
                <div style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                  background: requireApproval ? color : 'transparent',
                  border: `2px solid ${requireApproval ? color : C.muted}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {requireApproval && <span style={{ color: '#000', fontSize: 12, fontWeight: 900 }}>✓</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 600,
                    color: requireApproval ? color : C.text }}>Require Admin Approval</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2 }}>
                    Reports must be approved before becoming visible to all members
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error bar */}
        {err && (
          <div style={{ padding: '8px 18px', fontFamily: MONO, fontSize: 10, color: '#ef4444',
            background: '#ef444410', borderTop: `1px solid #ef444433`, flexShrink: 0 }}>
            {err}
          </div>
        )}

        {/* Footer nav */}
        <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.border}`,
          display: 'flex', gap: 10, flexShrink: 0, background: C.eqBg || C.bg }}>
          {section > 0 && (
            <button type="button" onClick={() => setSection(s => s - 1)}
              style={{ padding: '10px 18px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
                cursor: 'pointer', background: 'transparent',
                border: `1px solid ${C.border}`, color: C.muted }}>
              ← BACK
            </button>
          )}
          <div style={{ flex: 1 }} />
          {section < 3 ? (
            <button type="button" onClick={() => setSection(s => s + 1)}
              style={{ padding: '10px 22px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
                cursor: 'pointer', background: `${color}18`,
                border: `1px solid ${color}`, color }}>
              NEXT →
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={!canSubmit}
              style={{ padding: '10px 22px', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                opacity: busy ? 0.7 : 1,
                background: canSubmit ? `${C.accent}18` : 'transparent',
                border: `1px solid ${canSubmit ? C.accent : C.border}`,
                color: canSubmit ? C.accent : C.muted }}>
              {busy ? 'CREATING…' : 'CREATE PORTAL →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Add member modal (admin only)
// ══════════════════════════════════════════════════════════════════════════════
function AddMemberModal({ db, portal, allPortals, userName, C, MONO, onClose }) {
  const [name,  setName]  = useState('');
  const [email, setEmail] = useState('');
  const [portals, setPortals] = useState([portal.id]);
  const [busy,  setBusy]  = useState(false);
  const [msg,   setMsg]   = useState('');
  const [err,   setErr]   = useState('');

  function togglePortal(pid) {
    setPortals(prev => prev.includes(pid)
      ? (prev.length > 1 ? prev.filter(x => x !== pid) : prev)
      : [...prev, pid]);
  }

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setErr('Name and email are required'); return; }
    if (!portals.length) { setErr('Select at least one portal'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await addDoc(collection(db, 'access_requests'), {
        uid: null,
        name: name.trim(),
        email: email.trim(),
        team: portals[0],
        reason: `Added to ${portals.map(pid => allPortals?.find(p => p.id === pid)?.label || pid).join(', ')} by admin`,
        status: 'pre_approved',
        role: portals[0],
        portals,
        preApprovedEmail: email.trim().toLowerCase(),
        requestedAt: serverTimestamp(),
        approvedAt: serverTimestamp(),
        approvedBy: userName || 'Admin',
      });
      setMsg(`✓ ${name.trim()} added. They'll be auto-activated when they register with ${email.trim()}.`);
      setName(''); setEmail(''); setPortals([portal.id]);
    } catch (ex) {
      setErr(ex.message || 'Failed to add member');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#00000099', zIndex: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: C.panel, border: `1px solid ${C.border}`,
        width: '100%', maxWidth: 400, boxShadow: '0 24px 64px #000000bb' }}>

        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`,
          background: C.eqBg || C.bg }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 2,
            marginBottom: 3 }}>ADD MEMBER TO</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: MONO, fontSize: 13, color: portal.color }}>
              {portal.icon} {portal.label}
            </span>
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', color: C.muted,
                cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
          </div>
        </div>

        <form onSubmit={submit} style={{ padding: '18px 20px 22px' }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, lineHeight: 1.7,
            background: `${portal.color}10`, border: `1px solid ${portal.color}33`,
            padding: '8px 12px', marginBottom: 16 }}>
            Pre-approves the user. Their account will be auto-activated with access to every
            portal checked below when they register.
          </div>

          <FieldLabel MONO={MONO} C={C}>FULL NAME</FieldLabel>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
            style={inputStyle(C, MONO, 12)} />

          <FieldLabel MONO={MONO} C={C}>EMAIL ADDRESS</FieldLabel>
          <input value={email} onChange={e => setEmail(e.target.value)}
            type="email" placeholder="user@example.com"
            style={inputStyle(C, MONO, 12)} />

          <FieldLabel MONO={MONO} C={C}>PORTALS ({portals.length} selected)</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {(allPortals || [portal]).map(p => {
              const checked = portals.includes(p.id);
              return (
                <label key={p.id}
                  onClick={() => togglePortal(p.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                    fontFamily: MONO, fontSize: 10, padding: '5px 10px',
                    background: checked ? `${p.color}18` : 'transparent',
                    border: `1px solid ${checked ? p.color : C.border}`,
                    color: checked ? p.color : C.muted }}>
                  <span>{p.icon}</span>{p.label}
                </label>
              );
            })}
          </div>

          {err && <div style={{ fontFamily: MONO, fontSize: 10, color: '#ef4444', marginBottom: 10 }}>{err}</div>}
          {msg && <div style={{ fontFamily: MONO, fontSize: 10, color: '#10b981',
            marginBottom: 10, lineHeight: 1.65 }}>{msg}</div>}

          <button type="submit" disabled={busy}
            style={{ width: '100%', padding: '11px', fontFamily: MONO, fontSize: 10,
              letterSpacing: 1.5, cursor: busy ? 'wait' : 'pointer',
              background: `${portal.color}18`, border: `1px solid ${portal.color}`,
              color: portal.color }}>
            {busy ? 'ADDING…' : 'ADD MEMBER →'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Shared micro-helpers ──────────────────────────────────────────────────────
function FieldLabel({ children, MONO, C }) {
  return (
    <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1.5,
      marginBottom: 5 }}>{children}</div>
  );
}

function inputStyle(C, MONO, fontSize) {
  return {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    color: C.text, fontFamily: MONO, fontSize,
    padding: '9px 12px', marginBottom: 14, boxSizing: 'border-box',
  };
}
